import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spin, Tabs } from 'antd';
import { getOrderGenieRequest } from '../../services/orderXApi';
import { downloadAttachment, getInboxItem } from '../../services/emailInboxApi';
import DocumentFilePreview from './DocumentFilePreview';

// Defers mounting `children` until the wrapper div scrolls into view (with a 200px
// lookahead margin), so a request with many email attachments doesn't fetch/render
// every attachment's preview up front — only the ones the user actually scrolls to.
// IntersectionObserver's default root (viewport) still clips through the modal's own
// scrollable body, so this works without needing a ref to that scroll container.
function LazyVisible({ children, placeholderHeight = 120 }) {
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return undefined;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={containerRef}>
      {visible ? children : (
        <div
          className="text-muted small d-flex align-items-center justify-content-center"
          style={{ minHeight: placeholderHeight }}
        >
          Scroll to load preview…
        </div>
      )}
    </div>
  );
}

// Renders the email's raw HTML inside a sandboxed iframe (allow-same-origin only, no
// allow-scripts) rather than dangerouslySetInnerHTML, so an attacker-controlled inbound
// email body can't execute script or reach the parent page — same approach already used
// by EmailInboxDetail.js for the same untrusted-HTML problem.
function EmailHtmlBody({ html }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    const doc = frame.contentDocument || frame.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    const resize = () => {
      try { frame.style.height = `${doc.body.scrollHeight + 32}px`; } catch (err) { /* ignore */ }
    };
    resize();
    frame.addEventListener('load', resize);
    // eslint-disable-next-line consistent-return
    return () => frame.removeEventListener('load', resize);
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title="Email body"
      sandbox="allow-same-origin"
      style={{ width: '100%', minHeight: 200, maxHeight: 400, border: 'none' }}
    />
  );
}

// Lets the user preview the original uploaded document(s) and/or source email for a
// request from any step of the wizard, without leaving the current step — resolves
// source_files/source_email_id from the request row itself, since neither is carried
// in the wizard's own state (only items/matches/summary_json are hydrated there).
const RequestSourcePreviewModal = ({ requestId, userId, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceFiles, setSourceFiles] = useState([]);
  const [emailItem, setEmailItem] = useState(null);
  const [activeTab, setActiveTab] = useState('document');

  useEffect(() => {
    if (!open || requestId == null || !userId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSourceFiles([]);
    setEmailItem(null);

    (async () => {
      try {
        const fullRequest = await getOrderGenieRequest(requestId, userId);
        if (cancelled) return;
        const request = fullRequest?.request || {};
        const files = Array.isArray(request.source_files) ? request.source_files : [];
        setSourceFiles(files);
        setActiveTab(files.length ? 'document' : 'email');

        if (request.source_email_id) {
          try {
            const inboxItem = await getInboxItem(request.source_email_id);
            if (!cancelled) setEmailItem(inboxItem);
          } catch (err) {
            if (!cancelled) setEmailItem({ error: err?.message || 'Unable to load source email.' });
          }
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Unable to load request source.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, requestId, userId]);

  const hasEmail = Boolean(emailItem);
  const tabItems = [
    {
      key: 'document',
      label: `Document${sourceFiles.length > 1 ? `s (${sourceFiles.length})` : ''}`,
      children: sourceFiles.length === 0 ? (
        <div className="text-muted small">No source document is attached to this request.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {sourceFiles.map((file) => {
            const key = file.storage_path || file.filename;
            return (
              <div className="border rounded p-2" key={key}>
                <div className="fw-semibold small mb-2">{file.filename}</div>
                <DocumentFilePreview file={file} maxHeight="68vh" />
              </div>
            );
          })}
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Source Email',
      children: !hasEmail ? (
        <div className="text-muted small">This request was not created from an email.</div>
      ) : emailItem.error ? (
        <div className="text-danger small">{emailItem.error}</div>
      ) : (
        <div>
          <div className="mb-2">
            <div className="fw-semibold">{emailItem.subject || '(no subject)'}</div>
            <div className="text-muted small">
              From: {emailItem.from_name ? `${emailItem.from_name} <${emailItem.from_email}>` : emailItem.from_email}
              {emailItem.received_at ? ` — ${new Date(emailItem.received_at).toLocaleString()}` : ''}
            </div>
          </div>
          {emailItem.body_html ? (
            <div className="border rounded p-2">
              <EmailHtmlBody html={emailItem.body_html} />
            </div>
          ) : (
            <pre className="border rounded p-2 small" style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {emailItem.body_text || '(no body)'}
            </pre>
          )}
          {Array.isArray(emailItem.attachments) && emailItem.attachments.length > 0 && (
            <div className="mt-3">
              <div className="text-muted small mb-2">
                Attachments ({emailItem.attachments.length})
              </div>
              <div className="d-flex flex-column gap-3">
                {emailItem.attachments.map((att) => (
                  <div className="border rounded p-2" key={att.id}>
                    <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                      <div className="fw-semibold small text-truncate" title={att.filename}>{att.filename}</div>
                      <Button
                        size="small"
                        className="flex-shrink-0"
                        onClick={() => downloadAttachment(emailItem.id, att.id, att.filename)}
                      >
                        Download
                      </Button>
                    </div>
                    {att.storage_path ? (
                      <LazyVisible>
                        <DocumentFilePreview
                          file={{ storage_path: att.storage_path, filename: att.filename, content_type: att.content_type }}
                          maxHeight="50vh"
                        />
                      </LazyVisible>
                    ) : (
                      <div className="text-muted small">Preview unavailable for this attachment.</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={`Original source — Request #${requestId}`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width="90vw"
      style={{ maxWidth: 1400, top: 20 }}
      styles={{
        body: {
          resize: 'vertical',
          overflow: 'auto',
          minHeight: 320,
          maxHeight: '82vh',
          paddingBottom: 16,
        },
      }}
    >
      <div className="text-muted small mb-2">
        Drag the bottom-right corner of this area to resize it.
      </div>
      {loading ? (
        <div className="text-center py-4"><Spin /></div>
      ) : error ? (
        <div className="text-danger small">{error}</div>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      )}
    </Modal>
  );
};

export default RequestSourcePreviewModal;
